import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { generatePath } from 'react-router-dom'
import dayjs from 'dayjs'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { BRAND_KITS_PATH } from 'paths'
import { ApiError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import {
  openWeeklyPostLimitModal,
  WEEKLY_POST_LIMIT_ERROR_CODE,
} from 'components/WeeklyPostLimitReachedModal/openWeeklyPostLimitModal'
import { useAddToBrandPlanSchedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { usePreviewTopicsSync } from 'hooks/queries/schedules/usePreviewTopicsSync'
import { useCreateScheduleReferenceImage } from 'hooks/queries/schedules/useCreateScheduleReferenceImage'
import { useBrandKitAvatars } from 'hooks/queries/brandKits/useBrandKitAvatars'
import { isAvatarInRotation } from 'components/AvatarPickerModal/AvatarPickerModal'
import { BrandPlan, CampaignOption } from 'types'
import { ContentCategory } from 'utils/brandPlans/contentCategories'
import { computePostTimes, getDefaultTacticsForPostType, sampleTopicForPostType } from '../CreateContentModal/utils'
import { CampaignChipDropdown } from './CampaignChipDropdown'
import { ComposeRow } from './ComposeRow'
import { ComposeRowData } from './Types'
import { DEFAULT_VIDEO_VOICEOVER_VOICE } from 'screens/BrandKits/components/ContentPreferences/videoVoiceoverCaptionsConstants'
import { appendComposeRow, buildInitialComposeRow, expandComposeRowsToPayload } from './composeUtils'
import Styles from './ComposeRow.module.scss'
import { FilePlus1, Plus } from 'blaze-ui/icons'

interface ComposeStepProps {
  brandPlan: BrandPlan
  initialSchedule: CampaignOption
  schedules: CampaignOption[]
  isLoadingSchedules: boolean
  workspaceId: number
  onPostsCreated: (postsCount: number, brandPlanScheduleId: number) => void
  oneOffScheduleId?: number
  initialContentCategory?: ContentCategory
  scheduleSelectionEnabled?: boolean
}

export const ComposeStep = ({
  brandPlan,
  initialSchedule,
  schedules,
  isLoadingSchedules,
  workspaceId,
  onPostsCreated,
  oneOffScheduleId,
  initialContentCategory,
  scheduleSelectionEnabled = true,
}: ComposeStepProps) => {
  const { openModal, closeAllModals } = useModals()
  const [selectedScheduleId, setSelectedScheduleId] = useState(initialSchedule.id)
  const [composeRows, setComposeRows] = useState<ComposeRowData[]>(() => [
    buildInitialComposeRow(initialSchedule, false, initialContentCategory),
  ])
  // Always-current snapshot of composeRows, so async callbacks (topic-generation success, retry)
  // read the latest rows instead of a stale closure.
  const composeRowsRef = useRef(composeRows)
  composeRowsRef.current = composeRows

  const brandKitId = brandPlan.brand_kit?.id
  const brandKitCaptionStyle = brandPlan.brand_kit?.content_preferences?.video_caption_style
  const brandKitVoiceoverVoice =
    brandPlan.brand_kit?.content_preferences?.video_voiceover_voice ?? DEFAULT_VIDEO_VOICEOVER_VOICE
  const { data: brandKitAvatars } = useBrandKitAvatars({ brandKitId })
  const defaultAvatar = useMemo(() => (brandKitAvatars ?? []).filter(isAvatarInRotation)[0] ?? null, [brandKitAvatars])

  const resolvedRows = useMemo(
    () =>
      composeRows.map((r) =>
        !r.isBlank && r.contentCategory === 'avatar_video' && !r.avatar && defaultAvatar
          ? { ...r, avatar: defaultAvatar }
          : r,
      ),
    [composeRows, defaultAvatar],
  )

  const oneOffOption = useMemo<CampaignOption | undefined>(
    () =>
      oneOffScheduleId !== undefined
        ? {
            id: oneOffScheduleId,
            title: undefined,
            start_date: dayjs().format('YYYY-MM-DD'),
            end_date: dayjs().format('YYYY-MM-DD'),
            week_days: [],
            tactics: [],
            cover_image: undefined,
            kind: 'one_off',
          }
        : undefined,
    [oneOffScheduleId],
  )

  const resolveSchedule = (id: number | undefined): CampaignOption | undefined =>
    schedules.find((s) => s.id === id) ?? (id === oneOffScheduleId ? oneOffOption : undefined)

  const selectedSchedule = resolveSchedule(selectedScheduleId) ?? initialSchedule

  const { mutate: addMoreContent, isLoading: isMutating } = useAddToBrandPlanSchedule()
  const { mutate: createReferenceImage } = useCreateScheduleReferenceImage()

  const { mutateAsync: generatePreviewTopics } = usePreviewTopicsSync({
    onSuccess: (preview, variables) => {
      if (!preview.topics) return
      const scheduleId = variables?.scheduleId ?? selectedSchedule.id
      const prev = composeRowsRef.current
      const usedTopics = new Set(prev.filter((r) => !r.loading && r.topic).map((r) => r.topic))
      const assignedTopics: Array<{ id: string; topic: string }> = []
      let hasUnfilled = false
      const nextRows = prev.map((row) => {
        if (row.isBlank) return row
        if (!row.loading || row.userEdited) {
          return row.loading ? { ...row, loading: false } : row
        }
        const next = sampleTopicForPostType(preview.topics ?? {}, row.postType, usedTopics)
        if (next) {
          usedTopics.add(next)
          // Avatar rows show the avatar instead of a reference image — skip the fetch.
          if (row.contentCategory !== 'avatar_video') assignedTopics.push({ id: row.id, topic: next })
          return { ...row, topic: next, loading: false }
        }
        hasUnfilled = true
        return row
      })
      setComposeRows(nextRows)
      // Trigger the reference-image lookup here, at the topic-generation event — exactly once per
      // generated topic. Keeping this off a per-row effect is what prevents the re-fire storm.
      assignedTopics.forEach(({ id, topic }) => fetchReferenceImage(id, scheduleId, topic))
      if (hasUnfilled && variables?.scheduleId) {
        setTimeout(() => triggerTopicGeneration(variables.scheduleId), 0)
      }
    },
    onError: () => {
      setComposeRows((prev) => prev.map((row) => (row.loading ? { ...row, loading: false } : row)))
    },
  })

  const triggerTopicGeneration = useCallback(
    (scheduleId: number, contextSourceIds?: number[]) => {
      generatePreviewTopics({ scheduleId, contextSourceIds })
    },
    [generatePreviewTopics],
  )

  useEffect(() => {
    triggerTopicGeneration(initialSchedule.id)
  }, [initialSchedule.id, triggerTopicGeneration])

  const handleScheduleChange = (id: number) => {
    setSelectedScheduleId(id)
    const newSchedule = resolveSchedule(id)
    if (!newSchedule) return

    const postTimes = computePostTimes(newSchedule, composeRows.length)
    const hasAi = composeRows.some((r) => !r.isBlank)

    setComposeRows((prev) =>
      prev.map((row, i) => {
        const tactics = getDefaultTacticsForPostType(row.postType, newSchedule.tactics || [])
        const postAt = postTimes[i] ?? row.postAt
        if (row.isBlank) return { ...row, tactics, postAt }
        return {
          ...row,
          tactics,
          postAt,
          topic: '',
          loading: true,
          userEdited: false,
          referenceMedia: null,
          referenceMediaStatus: undefined,
        }
      }),
    )

    if (hasAi) triggerTopicGeneration(id)
  }

  const handleAddMore = (isBlank: boolean) => {
    setComposeRows((prev) => [...prev, appendComposeRow(selectedSchedule, prev, isBlank)])
    if (!isBlank) triggerTopicGeneration(selectedSchedule.id)
  }

  const updateRow = (id: string, updates: Partial<ComposeRowData>) => {
    setComposeRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)))
  }

  const fetchReferenceImage = (rowId: string, scheduleId: number, topic: string) => {
    if (!topic) return
    updateRow(rowId, { referenceMedia: null, referenceMediaStatus: 'loading' })
    createReferenceImage(
      { scheduleId, topic },
      {
        onSuccess: (data) =>
          updateRow(rowId, { referenceMedia: data, mediaFileIds: [data.key], referenceMediaStatus: 'success' }),
        onError: () => updateRow(rowId, { referenceMediaStatus: 'error' }),
      },
    )
  }

  const retryReferenceImage = (rowId: string) => {
    const row = composeRowsRef.current.find((r) => r.id === rowId)
    if (row?.topic) fetchReferenceImage(rowId, selectedSchedule.id, row.topic)
  }

  const removeRow = (id: string) => {
    setComposeRows((prev) => prev.filter((r) => r.id !== id))
  }

  const handleRegenerate = (id: string, contextSourceIds?: number[]) => {
    setComposeRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, topic: '', loading: true, userEdited: false, referenceMedia: null, referenceMediaStatus: undefined }
          : r,
      ),
    )
    triggerTopicGeneration(selectedSchedule.id, contextSourceIds)
  }

  const isAiLoading = composeRows.some((r) => !r.isBlank && r.loading)
  const hasAvatarRowMissingAvatar = resolvedRows.some(
    (r) => !r.isBlank && r.contentCategory === 'avatar_video' && !r.avatar,
  )

  const handleSubmit = () => {
    addMoreContent(
      {
        schedule: expandComposeRowsToPayload(resolvedRows),
        brandPlanId: brandPlan.id,
        brandPlanScheduleId: selectedSchedule.id,
      },
      {
        onSuccess: () => {
          onPostsCreated(composeRows.length, selectedSchedule.id)
        },
        onError: (error) => {
          if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
            const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
            showToastMessage(
              'Your content preferences require brand kit media, but none are uploaded.',
              'error',
              { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
              10,
            )
          } else if (error instanceof ApiError && error.errorCode === WEEKLY_POST_LIMIT_ERROR_CODE) {
            openWeeklyPostLimitModal(openModal, closeAllModals)
          } else {
            showErrorMessageToast(error instanceof Error ? error.message : 'An error occurred while creating posts.')
          }
        },
      },
    )
  }

  return (
    <>
      <Modal.Header
        subHeader="Create one or more posts from scratch or with AI. AI generated posts will auto suggest a topic from your campaign and select a reference image."
        subHeaderGap={12}
      >
        <div className={Styles.headerRow}>
          <Heading level={2}>New Post</Heading>
          {scheduleSelectionEnabled && (
            <CampaignChipDropdown
              schedules={schedules}
              selectedSchedule={selectedSchedule}
              isLoading={isLoadingSchedules}
              onSelect={handleScheduleChange}
              oneOffScheduleId={oneOffScheduleId}
            />
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.composeList}>
          {resolvedRows.map((row) => (
            <ComposeRow
              key={row.id}
              row={row}
              schedule={selectedSchedule}
              brandKitId={brandKitId}
              defaultCaptionStyle={brandKitCaptionStyle}
              defaultVoiceoverVoice={brandKitVoiceoverVoice}
              onUpdate={updateRow}
              onRemove={removeRow}
              onRegenerate={handleRegenerate}
              onRegenerateWithContext={handleRegenerate}
              onRetryReferenceImage={retryReferenceImage}
              canRemove={composeRows.length > 1}
            />
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <div className={Styles.addMoreRow}>
            <Button variant="secondary" frontIcon={Plus} size="lg" onPress={() => handleAddMore(false)}>
              New Post
            </Button>
            <Button variant="tertiary" frontIcon={FilePlus1} size="lg" onPress={() => handleAddMore(true)}>
              Blank Post
            </Button>
          </div>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right" className={Styles.footerRight}>
          <Button
            variant="primary"
            size="lg"
            isDisabled={isMutating || composeRows.length === 0 || isAiLoading || hasAvatarRowMissingAvatar}
            onPress={handleSubmit}
          >
            {isMutating ? 'Creating...' : `Schedule ${composeRows.length} Post${composeRows.length === 1 ? '' : 's'}`}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
